import * as React from "react";

export enum ReactCheckout2022Route {
  mybag = "mybag",
  delivery_details = "delivery-details",
  payment = "payment",
  success = "success",
}
export type ReactCheckout2022Routes = `${ReactCheckout2022Route}`;

interface ReactCheckout2022RouterConsumerInterface {
  currentRoute: ReactCheckout2022Routes;
}
const defaultValue = {} as unknown as ReactCheckout2022RouterConsumerInterface;
const ReactCheckout2022RouterContext = React.createContext(defaultValue);

interface ReactCheckout2022Router_ControllerInterface {
  navigateTo: (route: ReactCheckout2022Routes) => void;
}
const controllerDefaultValue =
  {} as unknown as ReactCheckout2022Router_ControllerInterface;
const ReactCheckout2022Router_ControllerContext = React.createContext(
  controllerDefaultValue
);

const HASHES = {
  MYBAG: "#mybag",
  SHIPPING: "#shipping",
  delivery_details: "#shipping",
  PAYMENT: "#payment",
};
export const ReactCheckout2022RouteHash = HASHES;
const figureOutRoute = () => {
  if (window.location.href.indexOf("checkout/onepage/success") > -1) {
    return ReactCheckout2022Route.success;
  }

  const matchingRouteEntryForCurrentWindowLocationHref = [
    [ReactCheckout2022Route.mybag, HASHES.MYBAG],
    [ReactCheckout2022Route.delivery_details, HASHES.delivery_details],
    [ReactCheckout2022Route.payment, HASHES.PAYMENT],
  ].find(
    ([_route, routeHashValue]) =>
      window.location.href.indexOf(`${routeHashValue}`) > -1
  ) as [`${ReactCheckout2022Route}`, string] | undefined;
  if (
    matchingRouteEntryForCurrentWindowLocationHref &&
    matchingRouteEntryForCurrentWindowLocationHref.length > 0
  ) {
    return matchingRouteEntryForCurrentWindowLocationHref[0];
  }

  return ReactCheckout2022Route.mybag;
};

export const ReactCheckout2022RouterProvider: React.FunctionComponent = (
  props
) => {
  const [currentRoute, setCurrentRoute] =
    React.useState<ReactCheckout2022RouterConsumerInterface["currentRoute"]>(
      figureOutRoute
    );

  const handleHashChange = React.useCallback(() => {
    setCurrentRoute(figureOutRoute());
  }, [setCurrentRoute]);
  React.useEffect(() => {
    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [handleHashChange]);

  const navigateTo: ReactCheckout2022Router_ControllerInterface["navigateTo"] =
    React.useCallback((route) => {
      const hash = ([
        [ReactCheckout2022Route.mybag, HASHES.MYBAG],
        [ReactCheckout2022Route.delivery_details, HASHES.delivery_details],
        [ReactCheckout2022Route.payment, HASHES.PAYMENT],
      ].find(([potentialRoute, hash]) => potentialRoute === route) || [
        ReactCheckout2022Route.mybag,
        HASHES.MYBAG,
      ])[1];
      (window as Window).location =
        ("" + window.location).replace(/#[A-Za-z0-9_]*$/, "") + `${hash}`;
    }, []);

  const value: ReactCheckout2022RouterConsumerInterface = React.useMemo(
    () => ({ currentRoute }),
    [currentRoute]
  );
  const controllerValue: ReactCheckout2022Router_ControllerInterface =
    React.useMemo(() => ({ navigateTo }), [navigateTo]);
  return (
    <ReactCheckout2022RouterContext.Provider value={value}>
      <ReactCheckout2022Router_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </ReactCheckout2022Router_ControllerContext.Provider>
    </ReactCheckout2022RouterContext.Provider>
  );
};

export const useReactCheckout2022Router = () => {
  const context = React.useContext(ReactCheckout2022RouterContext);
  if (context === defaultValue) {
    throw new Error(
      "useReactCheckout2022Router must be used within a ReactCheckout2022RouterProvider"
    );
  }
  return context;
};

export const useReactCheckout2022Router_asController = () => {
  const context = React.useContext(ReactCheckout2022Router_ControllerContext);
  if (context === controllerDefaultValue) {
    throw new Error(
      "useReactCheckout2022Router_asController must be used within a ReactCheckout2022RouterProvider"
    );
  }
  return context;
};
